import { useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import Dialog from "@/components/Dialog";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import {
  ADD_MEMBER_ELECTION_DATE_MUTATION,
  UPCOMING_MEMBER_ELECTION_DATES_QUERY,
  UPDATE_MEMBER_ELECTION_DATE_MUTATION
} from "../queries";
import type {
  AddMemberElectionDateMutationResponse,
  AddMemberElectionDateMutationVariables,
  UpcomingMemberElectionDateNode,
  UpdateMemberElectionDateMutationResponse,
  UpdateMemberElectionDateMutationVariables
} from "../types";

export type ElectionDateDialogProps = {
  open: boolean;
  mode: "add" | "edit";
  initialDate?: UpcomingMemberElectionDateNode;
  onClose(): void;
  onSaved?(date: string): void;
};

type DateRange = { startDate: string | null; endDate: string | null };

const emptyRange: DateRange = { startDate: null, endDate: null };

export default function ElectionDateDialog({ open, mode, initialDate, onClose, onSaved }: ElectionDateDialogProps) {
  const [dateRange, setDateRange] = useState<DateRange>(emptyRange);
  const focusSinkRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (open) {
      if (mode === "edit" && initialDate) {
        setDateRange({ startDate: initialDate.electionDate, endDate: initialDate.electionDate });
      } else {
        setDateRange(emptyRange);
      }
    }
  }, [open, mode, initialDate]);

  // md-dialog auto-focuses the first focusable child on open (DateSelect's
  // input), which would immediately open the calendar popover. Redirect
  // focus to a hidden sink so the DateSelect stays closed until clicked.
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => focusSinkRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const [addMutation, { loading: addLoading }] = useMutation<
    AddMemberElectionDateMutationResponse,
    AddMemberElectionDateMutationVariables
  >(ADD_MEMBER_ELECTION_DATE_MUTATION, {
    refetchQueries: [{ query: UPCOMING_MEMBER_ELECTION_DATES_QUERY }]
  });

  const [updateMutation, { loading: updateLoading }] = useMutation<
    UpdateMemberElectionDateMutationResponse,
    UpdateMemberElectionDateMutationVariables
  >(UPDATE_MEMBER_ELECTION_DATE_MUTATION, {
    refetchQueries: [{ query: UPCOMING_MEMBER_ELECTION_DATES_QUERY }]
  });

  const selectedDate = dateRange?.startDate ?? null;
  const isValid = !!selectedDate;
  const saving = addLoading || updateLoading;

  const handleSave = async () => {
    if (!selectedDate) return;

    try {
      if (mode === "add") {
        const { data } = await addMutation({ variables: { input: { electionDate: selectedDate } } });
        if (data?.addMemberElectionDate.success) {
          showSnackbar("Election date added");
          onSaved?.(selectedDate);
          onClose();
        } else {
          const apiMessage = data?.addMemberElectionDate.message;
          if (apiMessage) {
            showErrorSnackbar("Election date not added", { subtitle: apiMessage });
          } else {
            showErrorSnackbar("Election date not added", { subtitle: "Please try again later." });
          }
        }
      } else if (initialDate) {
        const { data } = await updateMutation({
          variables: { input: { id: initialDate.id, electionDate: selectedDate } }
        });
        if (data?.updateMemberElectionDate.success) {
          showSnackbar("Election date updated");
          onSaved?.(selectedDate);
          onClose();
        } else {
          const apiMessage = data?.updateMemberElectionDate.message;
          if (apiMessage) {
            showErrorSnackbar("Election date not updated", { subtitle: apiMessage });
          } else {
            showErrorSnackbar("Election date not updated", { subtitle: "Please try again later." });
          }
        }
      }
    } catch {
      showErrorSnackbar(mode === "add" ? "Election date not added" : "Election date not updated", {
        subtitle: "Please try again later."
      });
    }
  };

  const headline = mode === "add" ? "Add Election Date" : "Update Election Date";

  return (
    <Dialog
      open={open}
      headline={headline}
      onClosed={onClose}
      hasFormElements
      displayOverflow
      actions={
        <>
          <Button variant="text" onClick={onClose} testId="election-date-dialog-cancel">
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleSave}
            disabled={!isValid || saving}
            testId="election-date-dialog-save"
          >
            Save
          </Button>
        </>
      }
    >
      <div className="min-w-[360px]">
        <span ref={focusSinkRef} tabIndex={-1} aria-hidden="true" className="sr-only" />
        <DateSelect
          label="Election Date"
          placeholder="Select Upcoming Date"
          value={dateRange}
          onChange={(value) => {
            if (value && typeof value === "object") {
              setDateRange({
                startDate: (value.startDate ?? null) as string | null,
                endDate: (value.endDate ?? null) as string | null
              });
            } else {
              setDateRange(emptyRange);
            }
          }}
          useSingleDate
          useModal
          showFooter={false}
          minDate={new Date()}
        />
      </div>
    </Dialog>
  );
}
